import { PayloadAction, createReducer } from "@reduxjs/toolkit";
import { ISearchCallbackData } from "../../components/application/search/SearchBar";
import { IChemicalModel } from "../../data/ChemicalModel";
import type { IChemicalSearchModel } from "../../data/ChemicalSearchModel";
import type {
    ChemicalDataRequest,
    ChemicalDataResponse,
    ChemicalSearchRequest,
    ChemicalSearchResponse,
    WebSocketEstimateCompoundUpdate,
} from "../../data/models/schemas";
import {
    estimateCompound,
    getGraphs,
    GraphContextAction,
    loadCompounds,
    loadSavedSearches,
    removeCompound,
    replaceCompounds,
    searchCompounds,
    setGraphContext,
    setPropertiesTab,
    clearSearchResults,
    clearCompounds,
} from "../actions/pocketbook";
import { logOut } from "../actions/user";
import { IScoreWeights } from "./persistent/scoreWeights";

export type IPocketbookReduxState = {
    propertiesTab: number;
    graphContext: GraphContextAction;
    compounds: IChemicalModel[];
    searchResults: IChemicalSearchModel[];
    scoreWeights?: IScoreWeights;
};

const getDefaultState = (): IPocketbookReduxState => {
    return {
        propertiesTab: 0,
        graphContext: getGraphs().find((g) => g.default),
        compounds: [],
        searchResults: [],
    };
};

export type ISearchCompoundsRequest = {
    criteria?: ISearchCallbackData;
    requestContext?: ChemicalSearchRequest["requestContext"];
} & Omit<ChemicalSearchRequest, "criteria" | "requestContext">;

export type ISearchCompoundsResponse = ChemicalSearchResponse;

export type ILoadCompoundsRequest = {
    criteria?: ChemicalDataRequest["criteria"];
    requestContext?: ChemicalDataRequest["requestContext"] & {
        weights?: IScoreWeights;
    };
    estimationContext?: ChemicalDataRequest["estimationContext"];
} & Omit<
    ChemicalDataRequest,
    "criteria" | "requestContext" | "estimationContext"
>;

export type ILoadCompoundsResponse = ChemicalDataResponse;

export type IEstimateCompoundsResponse = Omit<
    WebSocketEstimateCompoundUpdate,
    "models" | "clearCompounds"
> & {
    models: IChemicalModel[];
    clearCompounds: boolean;
};

export const pocketbook = createReducer(getDefaultState(), (builder) => {
    builder
        .addCase(logOut.request, () => getDefaultState())
        .addCase(setPropertiesTab, (state, action) => {
            state.propertiesTab = parseInt(action.payload.toString(), 10);
        })
        .addCase(setGraphContext, (state, action) => {
            state.graphContext = action.payload;
        })
        .addCase(
            loadCompounds.success,
            (state, action: PayloadAction<ILoadCompoundsResponse>) => {
                const newCompounds =
                    (action.payload.models as IChemicalModel[] | undefined) ||
                    [];
                state.compounds = getCompounds(state.compounds, newCompounds, {
                    clearCompounds:
                        action.payload.requestContext?.clearCompounds === true,
                });
            }
        )
        .addCase(removeCompound, (state, action) => {
            state.compounds.splice(action.payload, 1);
        })
        .addCase(replaceCompounds, (state, action) => {
            state.compounds = action.payload.slice(0);
        })
        .addCase(clearCompounds, (state) => {
            state.compounds = [];
        })
        .addCase(estimateCompound.request, (state, action) => {
            state.compounds = getCompounds(
                state.compounds,
                action.payload.models,
                { clearCompounds: action.payload.clearCompounds },
                {
                    new_molecule:
                        action.payload.models[0].name.indexOf("CHEM_") >= 0,
                    still_loading: true,
                    ...getEstimationProgressProps(action.payload, 0, "REQUEST"),
                }
            );
        })
        .addCase(estimateCompound.update, (state, action) => {
            state.compounds = getCompounds(
                state.compounds,
                action.payload.models,
                { clearCompounds: false, onlyUpdateExisting: true },
                getEstimationProgressProps(action.payload)
            );
        })
        .addCase(estimateCompound.success, (state, action) => {
            state.compounds = getCompounds(
                state.compounds,
                action.payload.models,
                { clearCompounds: false, onlyUpdateExisting: true },
                {
                    still_loading: false,
                    ...getEstimationProgressProps(
                        action.payload,
                        100,
                        "SUCCESS"
                    ),
                }
            );
        })
        .addCase(estimateCompound.error, (state, action) => {
            state.compounds = getCompounds(
                state.compounds,
                action.payload.models,
                { clearCompounds: false },
                {
                    still_loading: false,
                    ...getEstimationProgressProps(
                        action.payload,
                        undefined,
                        "ERROR"
                    ),
                }
            );
        })
        .addCase(
            searchCompounds.success,
            (state, action: PayloadAction<ISearchCompoundsResponse>) => {
                state.searchResults = action.payload.models || [];
            }
        )
        .addCase(loadSavedSearches.success, (state) => {
            void state;
        })
        .addCase(clearSearchResults, (state) => {
            state.searchResults = [];
        });
});

type IGetCompoundsOptions = {
    clearCompounds?: boolean;
    onlyUpdateExisting?: boolean;
};

type IGetCompoundsExtraProps = {
    still_loading?: boolean;
    new_molecule?: boolean;
    estimation_progress?: IEstimateCompoundsResponse["percentComplete"];
    estimation_phase?: IEstimateCompoundsResponse["phase"];
    estimation_completed_tasks?: IEstimateCompoundsResponse["completedTasks"];
    estimation_total_tasks?: IEstimateCompoundsResponse["totalTasks"];
};

const getEstimationProgressProps = (
    response: IEstimateCompoundsResponse,
    defaultProgress?: IEstimateCompoundsResponse["percentComplete"],
    defaultPhase?: IEstimateCompoundsResponse["phase"]
): IGetCompoundsExtraProps => {
    const progress: IGetCompoundsExtraProps = {};
    const percentComplete = response.percentComplete ?? defaultProgress;
    const phase = response.phase ?? defaultPhase;

    if (percentComplete !== undefined) {
        progress.estimation_progress = percentComplete;
    }
    if (phase !== undefined) {
        progress.estimation_phase = phase;
    }
    if (response.completedTasks !== undefined) {
        progress.estimation_completed_tasks = response.completedTasks;
    }
    if (response.totalTasks !== undefined) {
        progress.estimation_total_tasks = response.totalTasks;
    }
    return progress;
};

export const getCompounds = (
    existingCompounds: IChemicalModel[],
    newCompounds: IChemicalModel[],
    options: IGetCompoundsOptions = {},
    additionalProps: IGetCompoundsExtraProps = {}
) => {
    if (options?.clearCompounds || existingCompounds.length === 0) {
        return newCompounds.map((c) => ({ ...c, ...additionalProps }));
    }

    const firstCompounds = existingCompounds.map((compound) => {
        const updatedCompound = newCompounds
            ? newCompounds.find((c) => compound.guid === c.guid)
            : null;

        if (updatedCompound) {
            return {
                ...compound,
                ...updatedCompound,
                ...additionalProps,
            };
        }
        return compound;
    });

    if (options?.onlyUpdateExisting) {
        return firstCompounds;
    }

    const newCompoundElements = newCompounds
        .filter((c) => !existingCompounds.find((e) => e.guid === c.guid))
        .map((c) => ({ ...c, ...additionalProps }));

    return [...firstCompounds, ...newCompoundElements];
};
